/**
 * Canonical repo layout for shelf documents, shared by the worker and the
 * frontend. Every path written to or read from the docs repo must go through
 * docPath() — the worker's sync, the editor's save, and the Notion importer
 * previously each used a different scheme ("methodologies/", "methodologys/",
 * and no prefix), which silently forked document families.
 */

/**
 * Shelf documents all live flat under this root inside the docs repo
 * (indexer-rust's `methodologies/docs/`). Document type is tracked in front
 * matter, not in the directory layout, so every doc type shares this root.
 */
export const DOCS_ROOT = "methodologies/docs";

export function docTypeDir(_docType: string): string {
  return DOCS_ROOT;
}

/**
 * Recover a doc_type hint from a repo path's leading directory. With the flat
 * layout the path no longer encodes the type, so this only ever returns a value
 * for legacy/type-named dirs; callers fall back to front matter's doc_type.
 */
export function docTypeFromDir(dir: string): string | undefined {
  const legacy: Record<string, string> = {
    methodologies: "methodology",
    factsheets: "factsheet",
    lois: "loi",
    memos: "memo",
    "research-notes": "researchNote",
    "governance-minutes": "governanceMinutes",
  };
  return legacy[dir];
}

export function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** `methodologies/<family>/<version>.md` */
export function docPath(
  docType: string,
  family: string,
  version: string,
): string {
  return `${docTypeDir(docType)}/${slugify(family) || family}/${version}.md`;
}

/** `methodologies/<family>/<version>.pdf` — the rendered, git-versioned PDF. */
export function pdfPath(
  docType: string,
  family: string,
  version: string,
): string {
  return docPath(docType, family, version).replace(/\.md$/, ".pdf");
}

/** Split a repo path into { family, version }; family includes the type dir. */
export function parseDocPath(path: string): {
  family: string;
  version: string;
} {
  const parts = path.replace(/\.md$/, "").split("/");
  const version = parts.pop() ?? "";
  return { family: parts.join("/"), version };
}

/** True when an editor route param refers to a repo file rather than a Notion page id. */
export function isRepoPath(id: string): boolean {
  return id.includes("/") && id.endsWith(".md");
}

/**
 * The canonical public website slug for a document — the path it is served at
 * under `/static/`. An explicit front-matter `slug` is the source of truth;
 * when absent we derive it from the family by taking the last path segment and
 * dropping the legacy `adjacent-` prefix. Decoupling this from the git family
 * lets a doc declare its public URL (e.g. `aic-charter`) independent of how its
 * folder is named.
 *
 * Factsheets are special-cased: their git family follows the
 * `<ticker>-factsheet-<period>` convention (e.g. `senr-factsheet-q2-2026`) but
 * they are served at `<ticker>/factsheet`, so an explicit-less factsheet family
 * derives to that nested path.
 */
export function websiteSlug(
  explicit: string | null | undefined,
  family: string,
): string {
  const s = (explicit ?? "").trim();
  if (s) return s;
  const last = family.split("/").pop() ?? family;
  const bare = last.replace(/^adjacent-/, "");
  const factsheet = bare.match(/^(.+?)-factsheet\b/);
  if (factsheet) return `${factsheet[1]}/factsheet`;
  return bare;
}
